import { useDataTableFeatures } from "@/components/data-table/hooks/useDataTableFeatures";
import { useDataTableSettings } from "@/components/data-table/hooks/useDataTableSettings";
import ExportDialog from "@/components/export/ExportDialog";
import ModKeyChar from "@/components/ModKeyChar";
import {
  ResponsiveMenu,
  ResponsiveMenuItem,
  ResponsiveMenuSeparator,
  ResponsiveMenuShortcut,
} from "@/components/ui/responsive-menu";
import { Wrench } from "lucide-react";
import { Module } from "@/constants";
import { buildExportQueryParams } from "@/lib/buildExportQueryParams";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { tinykeys } from "tinykeys";

interface QueryTableToolsMenuBarMenuProps extends PropsWithChildren {
  disabled?: boolean;
  inDialog?: boolean;
}

function QueryTableToolsMenuBarMenu({
  disabled,
  children,
}: QueryTableToolsMenuBarMenuProps) {
  const { t } = useTranslation();
  const { exportSettings } = useDataTableFeatures();
  const { options } = useDataTableSettings();
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  const onClick = useCallback(() => setExportDialogOpen(true), []);

  useEffect(() => {
    const unsubscribe = tinykeys(window, {
      "$mod+e": (event: KeyboardEvent) => {
        event.preventDefault();
        setExportDialogOpen(true);
      },
    });
    return () => {
      unsubscribe();
    };
  }, []);

  const moduleName = useMemo(() => {
    return exportSettings?.moduleName;
  }, [exportSettings]);

  const exportParams = useMemo(() => {
    const params = buildExportQueryParams(options.filters, options.sorting[0]);

    if (exportSettings?.queryParams) {
      Object.entries(exportSettings.queryParams).forEach(([key, value]) => {
        params.append(key, value);
      });
    }

    return params;
  }, [options.filters, options.sorting, exportSettings]);

  const { can_export } = useModulePermissions(moduleName as Module);

  return (
    <>
      <ResponsiveMenu
        label={t("listMenubar.tools.trigger")}
        icon={<Wrench className="h-5 w-5" />}
        disabled={disabled}
      >
        {children}
        {children && <ResponsiveMenuSeparator />}
        <ResponsiveMenuItem
          onClick={onClick}
          disabled={!moduleName || !can_export}
        >
          {t("listMenubar.tools.export")}
          <ResponsiveMenuShortcut>
            <ModKeyChar />E
          </ResponsiveMenuShortcut>
        </ResponsiveMenuItem>
      </ResponsiveMenu>
      {moduleName && (
        <ExportDialog
          open={exportDialogOpen}
          onOpenChange={setExportDialogOpen}
          moduleName={moduleName}
          params={exportParams}
        />
      )}
    </>
  );
}

export default QueryTableToolsMenuBarMenu;
